import { Box, Divider, Skeleton, Typography } from '@mui/material';
import CustomNotes from 'components/shared/CustomNotes';
import dayjs from 'dayjs';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { useCallback, useMemo } from 'react';
import { capitalizeStage, formatPipelineType } from '../DealsDetails.utils';
import type { OwnerOption } from '../hooks/useDealDetailsData';
import { useDealNotes } from '../hooks/useDealNotes';
import { DetailRow, DetailRowProps } from './DetailRow';
import { SectionHeader } from './SectionHeader';

export const ContentProductionDealDetails = ({
  dealData,
  loading,
  onFieldChange,
  ownerOptions = [],
  stageOptions = [],
  onActivityHistoryClick,
  completeOperation,
  startOperation,
}: {
  dealData: any;
  loading: boolean;
  onFieldChange?: (fieldName: string, newValue: string) => void;
  ownerOptions?: OwnerOption[];
  stageOptions?: string[];
  onActivityHistoryClick?: () => void;
  completeOperation: CompleteOperationType;
  startOperation: StartOperationType;
}) => {
  const manager = useDealNotes(dealData.dealId, startOperation, completeOperation);

  const systemInfo: DetailRowProps[] = useMemo(
    () => [
      {
        label: 'Created by',
        value: dealData?.createdBy || dealData?.createdByName || 'N/A',
        isAvatar: true,
      },
      {
        label: 'Created date',
        value:
          dealData?.createdDate || dealData?.createdAt
            ? dayjs(dealData.createdDate || dealData.createdAt).format('D MMMM, YYYY')
            : 'N/A',
      },
      {
        label: 'Last modified date',
        value:
          dealData?.lastModifiedDate || dealData?.updatedAt
            ? dayjs(dealData.lastModifiedDate || dealData.updatedAt).format('D MMMM, YYYY')
            : 'N/A',
        hasActivity: true,
      },
    ],
    [dealData],
  );

  const getFieldOptions = useCallback(
    (fieldName: string, currentValue: string) => {
      if (fieldName === 'stageName' && stageOptions.length > 0) {
        const options = stageOptions.map((stage) => ({
          value: stage.toLowerCase(),
          label: capitalizeStage(stage),
        }));

        if (
          currentValue &&
          currentValue !== 'N/A' &&
          !stageOptions.some((opt) => opt.toLowerCase() === currentValue.toLowerCase())
        ) {
          return [
            { value: currentValue.toLowerCase(), label: capitalizeStage(currentValue) },
            ...options,
          ];
        }
        return options;
      }

      if (fieldName === 'ownerName' && ownerOptions.length > 0) {
        const currentOwner = String(currentValue || '');
        if (currentOwner && !ownerOptions.find((o) => o.value === currentOwner)) {
          return [{ value: currentOwner, label: currentOwner }, ...ownerOptions];
        }
        return ownerOptions;
      }

      return undefined;
    },
    [ownerOptions, stageOptions],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          pb: 2,
          flex: 1,
          overflow: 'scroll',
          pr: 1,
          width: '100%',
          height: '100vh',
        }}
      >
        <SectionHeader title='Opportunity Information' />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Financial Information (Dual revenue)' />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Financial Information' />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Project Information' />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Attribution & Tracking' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Additional Information' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='System Information' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}
      </Box>
    );
  }

  if (!dealData) {
    return <Typography sx={{ p: 2 }}>No deal data available</Typography>;
  }

  const dealDetails = dealData.dealDetails || {};
  const flatDeal = { ...dealData, ...dealDetails };

  const renderFieldSection = (items: DetailRowProps[], sectionKey: string) => {
    if (items.length === 0) {
      return (
        <Typography variant='body2' sx={{ color: 'text.disabled', pl: 4 }}>
          No {sectionKey.toLowerCase()} available
        </Typography>
      );
    }

    return items.map((item, i) => {
      const currentValue = String(item.value || '');
      const options = onFieldChange
        ? getFieldOptions(item.fieldName || '', currentValue)
        : undefined;
      let displayValue = currentValue;
      if (item.fieldName === 'stageName') {
        const dealStageName = dealData?.stageName || dealData?.stage || '';
        const stageValue = currentValue && currentValue !== 'N/A' ? currentValue : dealStageName;
        if (stageValue && stageValue !== 'N/A') {
          displayValue = stageValue.toLowerCase();
        } else {
          displayValue = stageOptions[0]?.toLowerCase() || '';
        }
      }
      if (item.fieldName === 'pipelineType') {
        if (currentValue && currentValue !== 'N/A') {
          displayValue = formatPipelineType(currentValue);
        }
      }

      return (
        <DetailRow
          key={`${sectionKey}-${i}`}
          {...item}
          value={displayValue}
          onChange={onFieldChange}
          options={options}
        />
      );
    });
  };

  const opportunityInfoFields: DetailRowProps[] = [
    {
      label: 'Opportunity name',
      value: flatDeal.name || flatDeal.title || dealData.name || dealData.title || 'N/A',
      fieldName: 'name',
    },
    {
      label: 'Client name',
      value:
        flatDeal.clientName || flatDeal.client || dealData.clientName || dealData.client || 'N/A',
      fieldName: 'clientName',
      isAvatar: true,
    },
    {
      label: 'Opportunity owner',
      value: flatDeal.ownerName || flatDeal.owner || dealData.ownerName || dealData.owner || 'N/A',
      fieldName: 'ownerName',
      isAvatar: true,
      isSelect: true,
    },
    {
      label: 'Pipeline type',
      value:
        flatDeal.pipelineType ||
        flatDeal.dealType ||
        dealData.pipelineType ||
        dealData.dealType ||
        'N/A',
      fieldName: 'pipelineType',
      isChip: true,
    },
    {
      label: 'Stage',
      value: flatDeal.stageName || flatDeal.stage || dealData.stageName || dealData.stage || 'N/A',
      fieldName: 'stageName',
      isSelect: true,
    },
    {
      label: 'Probability (%)',
      value:
        flatDeal.probability || dealData.probability
          ? `${flatDeal.probability || dealData.probability}%`
          : 'N/A',
      fieldName: 'probability',
    },
    {
      label: 'Revenue tier',
      value: flatDeal.revenueTier || dealData.revenueTier || 'N/A',
      fieldName: 'revenueTier',
    },
    {
      label: 'Ceiling tier',
      value: flatDeal.ceilingTier || dealData.ceilingTier || 'N/A',
      fieldName: 'ceilingTier',
    },
    {
      label: 'Client orientation',
      value: flatDeal.clientOrientation || dealData.clientOrientation || 'N/A',
      fieldName: 'clientOrientation',
    },
    {
      label: 'Career stage',
      value:
        flatDeal.careerState ||
        flatDeal.careerStage ||
        dealData.careerState ||
        dealData.careerStage ||
        'N/A',
      fieldName: 'careerState',
    },
  ];

  const dualRevenueFields: DetailRowProps[] = [
    {
      label: 'Client talent fee ($)',
      value:
        flatDeal.clientTalentFee || dealData.clientTalentFee
          ? `$${(flatDeal.clientTalentFee || dealData.clientTalentFee).toLocaleString()}`
          : 'N/A',
      fieldName: 'clientTalentFee',
    },
    {
      label: 'Talent fee commission rate (%)',
      value:
        flatDeal.talentFeeCommissionRate || dealData.talentFeeCommissionRate
          ? `${flatDeal.talentFeeCommissionRate || dealData.talentFeeCommissionRate}%`
          : 'N/A',
      fieldName: 'talentFeeCommissionRate',
    },
    {
      label: 'Talent fee commission',
      value: flatDeal.talentFeeCommission || dealData.talentFeeCommission || 'N/A',
      fieldName: 'talentFeeCommission',
    },
    {
      label: '3AS production fee ($)',
      value:
        flatDeal['3ASProductionFee'] || dealData['3ASProductionFee']
          ? `$${(flatDeal['3ASProductionFee'] || dealData['3ASProductionFee']).toLocaleString()}`
          : 'N/A',
      fieldName: '3ASProductionFee',
    },
    {
      label: 'Total 3AS revenue',
      value: flatDeal.total3ASRevenue || dealData.total3ASRevenue || 'N/A',
      fieldName: 'total3ASRevenue',
    },
    {
      label: 'Owned revenue (%)',
      value:
        flatDeal.ownedRevenue || dealData.ownedRevenue
          ? `${flatDeal.ownedRevenue || dealData.ownedRevenue}%`
          : 'N/A',
      fieldName: 'ownedRevenue',
    },
  ];

  const financialInfoFields: DetailRowProps[] = [
    {
      label: 'Deal value (gross)',
      value:
        flatDeal.dealValue ||
        flatDeal.amount ||
        dealData.dealValue ||
        dealData.amount ||
        dealData.value ||
        'N/A',
      fieldName: 'dealValue',
      format: (v) => (v === 'N/A' ? 'N/A' : `$${v}`),
    },
    {
      label: 'Commission rate (%)',
      value:
        flatDeal.commissionRate || dealData.commissionRate
          ? `${flatDeal.commissionRate || dealData.commissionRate}%`
          : 'N/A',
      fieldName: 'commissionRate',
    },
    {
      label: 'Commission value',
      value: flatDeal.commissionValue || dealData.commissionValue || 'N/A',
      fieldName: 'commissionValue',
      format: (v) => (v === 'N/A' ? 'N/A' : `$${v}`),
    },
    {
      label: 'Deal structure',
      value: flatDeal.dealStructure || dealData.dealStructure || 'N/A',
      fieldName: 'dealStructure',
    },
    {
      label: 'Equity/ownership (%)',
      value: flatDeal.equity || dealData.equity ? `${flatDeal.equity || dealData.equity}%` : 'N/A',
      fieldName: 'equity',
    },
    {
      label: 'Backend',
      value: flatDeal.backend || dealData.backend || 'N/A',
      fieldName: 'backend',
    },
    {
      label: 'Expected close date',
      value:
        flatDeal.closeDate || dealData.closeDate
          ? new Date(flatDeal.closeDate || dealData.closeDate).toLocaleDateString()
          : 'N/A',
      fieldName: 'closeDate',
    },
  ];

  const projectInfoFields: DetailRowProps[] = [
    {
      label: 'Category',
      value: flatDeal.category || dealData.category || 'N/A',
      fieldName: 'category',
    },
    {
      label: 'Distribution Platforms',
      value: flatDeal.distributionPlatforms || dealData.distributionPlatforms || 'N/A',
      fieldName: 'distributionPlatforms',
    },
    {
      label: 'Production budget ($)',
      value:
        flatDeal.productionBudget || dealData.productionBudget
          ? `$${(flatDeal.productionBudget || dealData.productionBudget).toLocaleString()}`
          : 'N/A',
      fieldName: 'productionBudget',
    },
    {
      label: 'Projected margin (%)',
      value:
        flatDeal.projectedMargin || dealData.projectedMargin
          ? `${flatDeal.projectedMargin || dealData.projectedMargin}%`
          : 'N/A',
      fieldName: 'projectedMargin',
    },
    {
      label: 'Content rights',
      value: flatDeal.contentRights || dealData.contentRights || 'N/A',
      fieldName: 'contentRights',
    },
    {
      label: 'Payment terms',
      value: flatDeal.paymentTerms || dealData.paymentTerms || 'N/A',
      fieldName: 'paymentTerms',
    },
    {
      label: 'Project type',
      value: flatDeal.projectType || dealData.projectType || 'N/A',
      fieldName: 'projectType',
    },
    {
      label: 'Entertainment crossover',
      value: flatDeal.entertainmentCrossover || dealData.entertainmentCrossover || 'N/A',
      fieldName: 'entertainmentCrossover',
    },
    {
      label: 'LG/3AMs involved',
      value:
        flatDeal.lg3AMsInvolved !== undefined
          ? flatDeal.lg3AMsInvolved
            ? 'On'
            : 'Off'
          : dealData.lg3AMsInvolved
            ? 'On'
            : 'Off',
      fieldName: 'lg3AMsInvolved',
    },
    {
      label: 'Client role',
      value: flatDeal.clientRole || dealData.clientRole || 'N/A',
      fieldName: 'clientRole',
    },
    {
      label: 'Client ownership (%)',
      value:
        flatDeal.clientOwnership || dealData.clientOwnership
          ? `${flatDeal.clientOwnership || dealData.clientOwnership}%`
          : 'N/A',
      fieldName: 'clientOwnership',
    },
    {
      label: '3AS ownership (%)',
      value:
        flatDeal['3ASOwnership'] || dealData['3ASOwnership']
          ? `${flatDeal['3ASOwnership'] || dealData['3ASOwnership']}%`
          : 'N/A',
      fieldName: '3ASOwnership',
    },
    {
      label: 'Buyer/Platform',
      value: flatDeal.buyerPlatform || dealData.buyerPlatform || 'N/A',
      fieldName: 'buyerPlatform',
    },
  ];

  const attributionFields: DetailRowProps[] = [
    {
      label: 'Leadership sponsor',
      value: flatDeal.leadershipSponsor || dealData.leadershipSponsor || 'N/A',
      fieldName: 'leadershipSponsor',
    },
    { label: 'Source', value: flatDeal.source || dealData.source || 'N/A', fieldName: 'source' },
    {
      label: 'Partner account',
      value: flatDeal.partnerAccount || dealData.partnerAccount || 'N/A',
      fieldName: 'partnerAccount',
    },
    {
      label: 'Partner contact',
      value: flatDeal.partnerContact || dealData.partnerContact || 'N/A',
      fieldName: 'partnerContact',
    },
    {
      label: 'Days in current stage',
      value: flatDeal.daysInCurrentStage || dealData.daysInCurrentStage || 'N/A',
      fieldName: 'daysInCurrentStage',
    },
  ];

  const additionalInfoFields: DetailRowProps[] = [
    {
      label: 'Outcome',
      value: flatDeal.outcome || dealData.outcome || 'N/A',
      fieldName: 'outcome',
    },
    {
      label: 'Loss reason',
      value: flatDeal.lossReason || dealData.lossReason || 'N/A',
      fieldName: 'lossReason',
    },
    {
      label: 'Pass reason',
      value: flatDeal.passReason || dealData.passReason || 'N/A',
      fieldName: 'passReason',
    },
    {
      label: 'Competitor',
      value: flatDeal.competitor || dealData.competitor || 'N/A',
      fieldName: 'competitor',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        pb: 2,
        flex: 1,
        overflow: 'scroll',
        pr: 1,
        width: '100%',
        height: '100vh',
      }}
    >
      <SectionHeader title='Opportunity Information' />
      {renderFieldSection(opportunityInfoFields, 'opp')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Financial Information (Dual revenue)' />
      {renderFieldSection(dualRevenueFields, 'dualRevenue')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Financial Information' />
      {renderFieldSection(financialInfoFields, 'fin')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Project Information' />
      {renderFieldSection(projectInfoFields, 'project')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Attribution & Tracking' />
      {renderFieldSection(attributionFields, 'attr')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Additional Information' />
      {renderFieldSection(additionalInfoFields, 'add')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='System Information' />
      {systemInfo.map((item, i) => (
        <DetailRow key={`sys-${i}`} {...item} onActivityHistoryClick={onActivityHistoryClick} />
      ))}

      <CustomNotes manager={manager} />
    </Box>
  );
};
